import { Header } from "build-ts/metadata";
import React, { useEffect, useState } from "react";
import { useUser } from "context/UserContext";
import {
	Box,
	Button,
	Flex,
	HStack,
	Icon,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	Text,
	VStack,
	Spinner,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { BsRobot } from "react-icons/bs";
import { listScheduledJobs } from "services/ScheduleServices";
import AnalysisRunsViewer from "components/analyst/AnalysisRunsViewer";
import { FaClipboardList } from "react-icons/fa6";

interface ScheduledRunsProps {
	analystsList: Header[];
	isTabActive: boolean;
}

const ScheduledRuns: React.FC<ScheduledRunsProps> = ({ analystsList, isTabActive }) => {
	const { defaultAnalystId } = useUser();

	const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);
	const [scheduledJobs, setScheduledJobs] = useState<Header[]>([]);
	const [isLoadingJobs, setIsLoadingJobs] = useState(false);
	const [selectedJob, setSelectedJob] = useState<Header | null>(null);

	// Auto-select default analyst on mount / when analystsList loads
	useEffect(() => {
		if (analystsList.length === 0) return;
		const defaultAnalyst = analystsList.find(a => a.id === defaultAnalystId) || analystsList[0];
		setSelectedAnalyst(defaultAnalyst);
	}, [defaultAnalystId, analystsList]);

	// Fetch scheduled jobs whenever the selected analyst changes
	useEffect(() => {
		if (!selectedAnalyst?.id || !isTabActive) return;
		const fetchJobs = async () => {
			setIsLoadingJobs(true);
			setScheduledJobs([]);
			setSelectedJob(null);
			const response = await listScheduledJobs(
				"SCENARIO_JOB",
				-1,
				1,
				selectedAnalyst.id,
				"LAST_MODIFIED",
				false,
				true,
				"",
			);
			setScheduledJobs(response || []);
			setIsLoadingJobs(false);
		};
		fetchJobs();
	}, [selectedAnalyst?.id, isTabActive]);

	const handleAnalystSelect = (analyst: Header | null) => {
		setSelectedAnalyst(analyst);
	};

	const handleJobSelect = (job: Header | null) => {
		setSelectedJob(job);
	};

	return (
		<VStack align="stretch" spacing={4}>
			{/* Analyst & Analysis Selectors */}
			<Flex gap={3} align="flex-end">
				{/* Analyst - 50% */}
				<Box flex={1}>
					<Flex align="center" gap={2} mb={2}>
						<Icon as={BsRobot} boxSize={5} color="purple.700" />
						<Text fontSize="md" fontWeight="600" color="gray.600" m={0}>
							Analyst
						</Text>
					</Flex>
					<Menu>
						<MenuButton
							as={Button}
							size="sm"
							variant="outline"
							colorScheme="purple"
							bg="purple.50"
							borderColor="purple.50"
							_hover={{ bg: "purple.100", borderColor: "purple.100" }}
							borderRadius="lg"
							borderWidth="1px"
							px={3}
							py={1}
							w="100%"
							justifyContent="flex-start"
						>
							<HStack spacing={2} justify="space-between" w="100%">
								<HStack spacing={2}>
									<Text fontSize="sm" color="fg.default" fontWeight="600" m={0} noOfLines={1}>
										{selectedAnalyst ? selectedAnalyst.name : "Select analyst"}
									</Text>
								</HStack>
								<Icon as={ChevronDownIcon} boxSize={5} />
							</HStack>
						</MenuButton>
						<MenuList maxH="250px" overflowY="auto" minW="200px" borderColor="purple.200" boxShadow="lg">
							{analystsList.map((analyst) => (
								<MenuItem
									key={analyst.id}
									fontSize="sm"
									bg={selectedAnalyst?.id === analyst.id ? "purple.700" : "transparent"}
									_hover={{ bg: selectedAnalyst?.id === analyst.id ? "purple.600" : "purple.50" }}
									onClick={() => handleAnalystSelect(analyst)}
								>
									<HStack spacing={2}>
										<Icon as={BsRobot} boxSize={5} color={selectedAnalyst?.id === analyst.id ? "white" : "purple.700"} />
										<Text fontSize="sm" color={selectedAnalyst?.id === analyst.id ? "white" : "fg.default"} fontWeight="500" m={0} noOfLines={1}>
											{analyst.name}
										</Text>
									</HStack>
								</MenuItem>
							))}
						</MenuList>
					</Menu>
				</Box>

				{/* Analysis - 50% */}
				<Box flex={1}>
					<Flex align="center" gap={2} mb={2}>
						<Icon as={FaClipboardList} boxSize={5} color="purple.700" />
						<Text fontSize="md" fontWeight="600" color="gray.600" m={0}>
							Analysis
						</Text>
					</Flex>
					{isLoadingJobs ? (
						<Flex align="center" gap={2} h="32px" borderRadius="full" bg="purple.50" px={3}>
							<Spinner size="sm" color="purple.600" thickness="2px" />
							<Text fontSize="sm" color="gray.400" m={0}>Loading...</Text>
						</Flex>
					) : (
						<Menu>
							<MenuButton
								as={Button}
								size="sm"
								variant="outline"
								colorScheme="purple"
								bg="purple.50"
								borderColor="purple.50"
								_hover={{ bg: "purple.100", borderColor: "purple.100" }}
								borderRadius="lg"
								borderWidth="1px"
								px={3}
								py={1}
								w="100%"
								justifyContent="flex-start"
								isDisabled={scheduledJobs.length === 0}
							>
								<HStack spacing={2} justify="space-between" w="100%">
									<HStack spacing={2}>
										<Text fontSize="sm" color="fg.default" fontWeight="600" m={0} noOfLines={1}>
											{selectedJob ? selectedJob.name : "Select analysis"}
										</Text>
									</HStack>
									<Icon as={ChevronDownIcon} boxSize={5} />
								</HStack>
							</MenuButton>
							<MenuList maxH="250px" overflowY="auto" minW="200px" borderColor="purple.200" boxShadow="lg">
								<MenuItem
									fontSize="sm"
									bg={!selectedJob ? "purple.700" : "transparent"}
									_hover={{ bg: !selectedJob ? "purple.600" : "purple.50" }}
									onClick={() => handleJobSelect(null)}
								>
									<HStack spacing={2}>
										<Text fontSize="sm" color={!selectedJob ? "white" : "fg.default"} fontWeight="500" m={0}>
											Select analysis
										</Text>
									</HStack>
								</MenuItem>
								{scheduledJobs.length > 0 && <MenuDivider />}
								{scheduledJobs.map((job) => (
									<MenuItem
										key={job.id}
										fontSize="sm"
										bg={selectedJob?.id === job.id ? "purple.700" : "transparent"}
										_hover={{ bg: selectedJob?.id === job.id ? "purple.600" : "purple.50" }}
										onClick={() => handleJobSelect(job)}
									>
										<HStack spacing={2}>
											<Icon as={FaClipboardList} boxSize={5} color={selectedJob?.id === job.id ? "white" : "purple.700"} />
											<Text fontSize="sm" color={selectedJob?.id === job.id ? "white" : "fg.default"} fontWeight="500" m={0} noOfLines={1}>
												{job.name}
											</Text>
										</HStack>
									</MenuItem>
								))}
							</MenuList>
						</Menu>
					)}
					{!isLoadingJobs && scheduledJobs.length === 0 && selectedAnalyst && (
						<Text fontSize="xs" color="gray.400" mt={1} ml={1}>
							No scheduled jobs found for this analyst.
						</Text>
					)}
				</Box>
			</Flex>

			{/* Job Runs Viewer */}
			{selectedJob && (
				<Box>
					<AnalysisRunsViewer selectedJob={selectedJob} />
				</Box>
			)}
		</VStack>
	);
};

export default ScheduledRuns;
